/**
 * The backend-minted JWE ("encryptedToken") — single source of truth.
 *
 * In Okta mode every clinical API call carries this JWE, not the raw Okta access
 * token. It is minted by `POST /auth/token-verification`, which JWE-wraps ONE
 * SPECIFIC Okta access token (`disease_mgmt/atman/auth/blueprint.py`
 * `token_verification`). The backend unwraps it and introspects that token
 * against Okta on every request (`disease_mgmt/atman/flask/okta_auth.py`
 * `validate_okta_token`) — so a JWE wrapping an access token Okta has rotated
 * away introspects `active=false` and the request comes back 401.
 *
 * Two invariants this module exists to hold:
 *
 * 1. ONE cache, not two. The JWE was held in BOTH `sessionStorage` and Redux,
 *    written by different code paths: `httpInterceptor` wrote both, while
 *    `okta-navbar`'s `renewed` handler wrote only Redux. After an Okta rotation
 *    the `sessionStorage` copy still wrapped the OLD access token, and any path
 *    that read it (a page reload, where Redux starts empty) served that stale
 *    JWE straight back to the backend -> 401 -> hard logout, no warning dialog.
 *    Reads go to `sessionStorage` and happen fresh per request, so no React
 *    closure can serve a stale copy either.
 *
 * 2. The cache is invalidated by the EVENT that makes it stale — Okta's
 *    `renewed` — not by a wall clock decoupled from the token it wraps. The
 *    15-minute age cap is kept only as a backstop for missed events.
 */

const TOKEN_KEY = 'encryptedToken'
const TOKEN_TS_KEY = 'encryptedTokenTS'

/** Backstop only. Eviction is normally driven by Okta's `renewed` event. */
export const BACKEND_TOKEN_MAX_AGE_MS = 900000

/**
 * Drop the cached JWE. Call this the moment the Okta access token it wraps is
 * renewed or rejected — the next request then mints a fresh one.
 */
export const clearBackendToken = (): void => {
  window.sessionStorage.removeItem(TOKEN_KEY)
  window.sessionStorage.removeItem(TOKEN_TS_KEY)
}

/** The cached JWE, or null if absent or past the backstop age. */
export const readBackendToken = (now: number = Date.now()): string | null => {
  const ts = window.sessionStorage.getItem(TOKEN_TS_KEY)
  if (ts && now - parseInt(ts, 10) > BACKEND_TOKEN_MAX_AGE_MS) {
    clearBackendToken()
    return null
  }
  return window.sessionStorage.getItem(TOKEN_KEY) || null
}

/** Store a freshly minted JWE and stamp it for the backstop age check. */
export const writeBackendToken = (token: string, now: number = Date.now()): void => {
  window.sessionStorage.setItem(TOKEN_KEY, token)
  window.sessionStorage.setItem(TOKEN_TS_KEY, `${now}`)
}
